import { useMutation, useQueryClient } from 'react-query';
import {
  AnnouncementData,
  SimpleAnnouncementData,
  SimpleAnnouncementPayload,
} from './types';
import { CustomAxios, getResponseData } from '../../redux/axios/axios';
import { REACT_APP_API_URL } from '../../config/app';
import {
  API as AnnouncementAPI,
  GetAnnouncementsResponse,
  UnpublishAnnouncementResponse,
  PublishAnnouncementResponse,
  CreateAnnouncementResponse,
} from '@weave/schema-gen-ts/dist/schemas/announcements/v1/announcements.pb';
import { authorizedFetch } from '../../helpers/utils';
import { getName } from './utils';

export const useAnnouncementUpsertMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      data,
      mode,
    }:
      | { data: AnnouncementData; mode: 'create' }
      | {
          data: AnnouncementData;
          update: Partial<AnnouncementData>;
          mode: 'update';
        }) => {
      console.log('Saving announcement:', data);
    },
    onMutate: (variables) => {
      const oldData = queryClient.getQueryData<AnnouncementData[]>(['announcements']);
      let updatedData: AnnouncementData[] = [];

      if (variables.mode === 'create') {
        updatedData = oldData ? [...oldData, variables.data] : [variables.data];
      } else {
        // Update existing announcement
        updatedData =
          oldData?.map((ann) => {
            if (ann.id === variables.data.id) {
              return {
                ...ann,
                ...variables.update,
              };
            }
            return ann;
          }) || [];
      }

      // Optimistically update the UI
      queryClient.setQueryData<AnnouncementData[]>(['announcements'], updatedData);

      return { oldData };
    },
    onError: (err, variables, context) => {
      // Revert optimistic updates if any
      if (context?.oldData) {
        queryClient.setQueryData<AnnouncementData[]>(['announcements'], context.oldData);
      }
    },
  });
};

const baseUrl = REACT_APP_API_URL;

export const useSimpleAnnouncementPublishMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      announcementId,
      setPublished,
    }: {
      announcementId: string;
      setPublished: boolean;
    }) => {
      console.log('Publishing announcement with ID:', announcementId);

      if (!setPublished) {
        const res = await AnnouncementAPI.UnpublishAnnouncement(
          authorizedFetch<UnpublishAnnouncementResponse>(),
          {
            id: announcementId,
          }
        );

        return res;
      }

      return await AnnouncementAPI.PublishAnnouncement(
        authorizedFetch<PublishAnnouncementResponse>(),
        {
          id: announcementId,
        }
      );
    },

    onMutate: ({ announcementId, setPublished }) => {
      const oldData = queryClient.getQueryData<SimpleAnnouncementData[]>([
        'announcements',
      ]);
      let updatedData: Omit<SimpleAnnouncementData, 'history'>[] = [];

      // Update existing announcement
      updatedData =
        oldData?.map((ann) => {
          if (ann.id === announcementId) {
            return {
              ...ann,
              isPublished: setPublished,
            };
          }
          return ann;
        }) || [];

      // Optimistically update the UI
      queryClient.setQueryData<Omit<SimpleAnnouncementData, 'history'>[]>(
        ['announcements'],
        updatedData
      );

      return { oldData };
    },
    onError: (err, variables, context) => {
      // Revert optimistic updates if any
      if (context?.oldData) {
        queryClient.setQueryData<Omit<SimpleAnnouncementData, 'history'>[]>(
          ['announcements'],
          context.oldData
        );
      }
    },
    onSuccess: (data) => {
      console.log('Publish/unpublish successful:', data);
      // Invalidate and refetch announcements after publishing
      // queryClient.invalidateQueries(['announcements']);

      // update the publish history entry
      queryClient.setQueryData<SimpleAnnouncementData[]>(['announcements'], (oldData) => {
        return (
          oldData?.map((ann) => {
            if (ann.id === data.announcement?.id) {
              return {
                ...ann,
                history: data.announcement?.publishHistory ?? [],
              };
            }
            return ann;
          }) || []
        );
      });
    },
  });
};

export const useSimpleAnnouncementUpsertMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      data,
      mode,
    }:
      | { data: Omit<SimpleAnnouncementData, 'history'>; mode: 'create' }
      | {
          data: Omit<SimpleAnnouncementData, 'history'>;
          update: Partial<Omit<SimpleAnnouncementData, 'history'>>;
          mode: 'update';
        }) => {
      console.log('Saving announcement:', data);

      const payload: SimpleAnnouncementPayload = {
        title: data.title,
        message: data.message,
        type: data.type,
        path: data.path,
        priority: data.priority,
        action: data.action,
      };

      return await AnnouncementAPI.CreateAnnouncement(
        authorizedFetch<CreateAnnouncementResponse>(),
        {
          name: data.title,
          payload,
        }
      );
    },
    onMutate: (variables) => {
      const oldData = queryClient.getQueryData<SimpleAnnouncementData[]>([
        'announcements',
      ]);
      let updatedData: Omit<SimpleAnnouncementData, 'history'>[] = [];

      if (variables.mode === 'create') {
        const newData: SimpleAnnouncementData = { ...variables.data, history: [] };
        updatedData = oldData ? [...oldData, newData] : [newData];
      } else {
        // Update existing announcement
        updatedData =
          oldData?.map((ann) => {
            if (ann.id === variables.data.id) {
              return {
                ...ann,
                ...variables.update,
              };
            }
            return ann;
          }) || [];
      }

      // Optimistically update the UI
      queryClient.setQueryData<Omit<SimpleAnnouncementData, 'history'>[]>(
        ['announcements'],
        updatedData
      );

      return { oldData };
    },
    onError: (err, variables, context) => {
      // Revert optimistic updates if any
      if (context?.oldData) {
        queryClient.setQueryData<Omit<SimpleAnnouncementData, 'history'>[]>(
          ['announcements'],
          context.oldData
        );
      }
    },
    onSuccess: (data, variables) => {
      console.log('Upsert successful:', data);
      // Update the ID of the newly created announcement
      if (variables.mode === 'create') {
        queryClient.setQueryData<SimpleAnnouncementData[]>(
          ['announcements'],
          (oldData) => {
            return (
              oldData?.map((ann) => {
                // check for the temp id
                if (ann.id === variables.data.id && data.announcement) {
                  const announcement = data.announcement;
                  const newData: SimpleAnnouncementData = {
                    id: announcement.id ?? '',
                    isPublished: announcement.isPublished ?? false,
                    title: announcement.name ?? '',
                    message: announcement.payload?.message ?? '',
                    createdBy: announcement.createdByUser
                      ? getName(announcement.createdByUser)
                      : 'Unknown User',
                    createdOn: announcement.createdAt ?? '',
                    type: announcement.payload?.type ?? 'info',
                    priority: announcement.payload?.priority ?? 0,
                    action: announcement.payload?.action,
                    history: announcement.publishHistory ?? [],
                  };

                  return newData;
                }
                return ann;
              }) || []
            );
          }
        );
      }
    },
  });
};

export const useSimpleAnnouncementDeleteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (announcementId: string) => {
      console.log('Deleting announcement with ID:', announcementId);

      return await AnnouncementAPI.DeleteAnnouncement(authorizedFetch(), {
        id: announcementId,
      });
    },

    onMutate: (announcementId: string) => {
      const oldData = queryClient.getQueryData<SimpleAnnouncementData[]>([
        'announcements',
      ]);
      let updatedData: Omit<SimpleAnnouncementData, 'history'>[] = [];

      // Remove the deleted announcement
      updatedData = oldData?.filter((ann) => ann.id !== announcementId) || [];

      // Optimistically update the UI
      queryClient.setQueryData<Omit<SimpleAnnouncementData, 'history'>[]>(
        ['announcements'],
        updatedData
      );

      return { oldData };
    },
    onError: (err, announcementId, context) => {
      // Revert optimistic updates if any
      if (context?.oldData) {
        queryClient.setQueryData<Omit<SimpleAnnouncementData, 'history'>[]>(
          ['announcements'],
          context.oldData
        );
      }
    },
  });
};
